import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { fetchWorkspaceBranding, removeWorkspaceLogo, resetWorkspaceBranding, updateWorkspaceBranding, uploadWorkspaceLogo, type WorkspaceBrandingView } from '../../api/brandingApi';
import { useBranding } from '../../context/BrandingContext';
import { EmptyState, PageHeader, Pill, ui } from '../../design-system';
import { BrandingForm } from './BrandingForm';

/** Settings › Branding (SRS #38): the workspace's own logo, colours, watermark and footer — stored on the
 *  server so team members, the app header and every report use it. Owner edits; members see. */
const BrandingPage: React.FC = () => {
  const { refresh } = useBranding();
  const [view, setView] = useState<WorkspaceBrandingView | null>(null);
  const [locked, setLocked] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    try { setView(await fetchWorkspaceBranding()); setLocked(null); }
    catch (e) {
      const err = e as Error & { status?: number };
      if (err.status === 403) setLocked(err.message);   // feature_locked → the plan message from the API
      else setError(err.message || 'Could not load branding');
    }
  }, []);
  useEffect(() => { void load(); }, [load]);

  const after = async () => { await load(); await refresh(); };

  if (locked) {
    return (
      <div style={ui.page}>
        <PageHeader eyebrow="Settings" title="Branding" subtitle="Your own logo, colours and report branding." />
        <EmptyState title="White-labelling is not part of your plan" hint={locked} action={<Link to="/pricing" state={{ changeOnly: true }} style={ui.primary}>See plans</Link>} />
      </div>
    );
  }
  if (error) return <div style={ui.page}><div style={ui.alert('danger')}>{error}</div></div>;
  if (!view) return <div style={ui.page}><div style={ui.muted}>Loading…</div></div>;

  // what an empty field inherits: the platform brand (never the workspace's own values)
  const inherited = view.effective.source === 'workspace'
    ? { ...view.effective, brand_name: 'Policyora', tagline: 'Global Policy Intelligence', primary_color: '#444ce7', accent_color: '#3538cd', watermark_enabled: true, watermark_text: 'CONFIDENTIAL', footer_text: '' }
    : view.effective;

  return (
    <div style={ui.page}>
      <PageHeader eyebrow="Settings" title="Branding"
        subtitle={<>Your logo, colours, watermark and footer — used in the app header and on every report your workspace generates. Empty fields inherit the platform brand.</>}
        actions={<Pill tone={view.effective.source === 'workspace' ? 'success' : 'neutral'}>{view.effective.source === 'workspace' ? 'Custom brand active' : 'Platform brand'}</Pill>} />
      {!view.is_owner && <div style={ui.alert('info')}>Only the workspace owner can change branding. You are seeing what your team uses.</div>}
      <BrandingForm stored={view.branding} inherited={inherited} effective={view.effective} limits={view.limits} readOnly={!view.is_owner} showCreditToggle
        onSave={async (patch) => { await updateWorkspaceBranding(patch); await after(); }}
        onUpload={async (f) => { await uploadWorkspaceLogo(f); await after(); }}
        onRemoveLogo={async () => { await removeWorkspaceLogo(); await after(); }}
        onReset={async () => { await resetWorkspaceBranding(); await after(); }} />
    </div>
  );
};

export default BrandingPage;
